import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Spin, Empty, Button, message, Tag } from "antd";
import {
  ArrowLeftOutlined,
  ApartmentOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import {
  JobApplicationStatusEnum,
  JobApplicationStatusLabel,
  JobApplicationStatusText,
} from "Constants/Enums/JobApplicantStatusEnum";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";
import ApplicantInterviewRounds from "Pages/EmployeeDashboard/ShortListEmployeeForm/ApplicantInterviewRounds";

const ACCENT = "#0f5a2a";

// Per the May-19 doc, internal applicants have ONLY ONE action across the
// entire pipeline: pick a slot during InterviewInProgress. (No Accept Terms,
// no Document send-back — those are external-only.)
function AppliedJobDetail() {
  const { applicantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState(null);

  const fetchApplication = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(API.hiringJobApplicant.getById(applicantId));
      setApp(res.data);
    } catch (err) {
      console.error("Error loading application detail:", err);
      message.error("Could not load application details.");
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [applicantId]);

  useEffect(() => {
    if (applicantId) fetchApplication();
  }, [applicantId, fetchApplication]);

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <DashboardNavbar />
        <div className="body flex-grow-1 px-3">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/employeedashboard/explore-careers")}
            style={{ paddingLeft: 0, color: ACCENT, fontWeight: 600, marginBottom: 8 }}
          >
            Back to Explore Careers
          </Button>

          {loading ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          ) : !app ? (
            <Empty description="Application not found." style={{ padding: "60px 0" }} />
          ) : (
            <DetailContent app={app} />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function DetailContent({ app }) {
  // Pipeline status field name differs across endpoints:
  //   /my-applications (list)   → `applicationStatus`
  //   /Get-By-Id      (detail)  → top-level `status` (per Mudassir May-22)
  // Posting Open/Closed always lives at `jobPosting.status`.
  const status =
    app.applicationStatus ??
    app.jobApplicationStatus ??
    app.jobApplicantStatus ??
    app.currentStatus ??
    app.pipelineStatus ??
    app.status;
  const title = app.jobTitle || app.forJobApply || "Applied Role";
  const department = app.departmentName || app.department;
  const location = app.jobLocationType || app.location;
  const jobType = app.jobType;
  const appliedOn = app.appliedOn || app.createdOn || app.applicationDate;
  const statusLabel = JobApplicationStatusLabel[status] || "Pending";
  const statusText = JobApplicationStatusText[status];

  const jobApplicationId = app.jobApplicantId || app.id;
  // Posting Open/Filled is always at `jobPosting.status`. Do NOT fall back
  // to top-level `app.status` here — that's the pipeline status (different
  // enum) and would mis-flag Shortlist (1) as Filled.
  const isPostingFilled =
    app.jobPosting?.status === JobPostingStatusEnum.Filled;
  const showInterviewRounds =
    !isPostingFilled && status === JobApplicationStatusEnum.InterviewInProgress;
  const isReject = status === JobApplicationStatusEnum.Reject;

  return (
    <>
      {/* Hero — job summary */}
      <Card
        style={{
          borderRadius: 20,
          marginBottom: 16,
          background: "linear-gradient(135deg, #0f5a2a 0%, #1e8c45 100%)",
          color: "white",
          border: "none",
          boxShadow: "0 8px 20px rgba(15,90,42,0.18)",
        }}
        styles={{ body: { padding: 24 } }}
      >
        <div
          style={{
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            opacity: 0.85,
            fontWeight: 600,
          }}
        >
          Internal Application
        </div>
        <div style={{ fontSize: "1.6rem", fontWeight: 700, marginTop: 4 }}>{title}</div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
          {department && (
            <HeroChip icon={<ApartmentOutlined />} text={department} />
          )}
          {location && <HeroChip icon={<EnvironmentOutlined />} text={location} />}
          {jobType && <HeroChip icon={<ClockCircleOutlined />} text={jobType} />}
          {appliedOn && (
            <HeroChip
              icon={<CalendarOutlined />}
              text={`Applied ${formatDate(appliedOn)}`}
            />
          )}
        </div>
      </Card>

      {/* Status card */}
      <Card
        style={{
          borderRadius: 18,
          marginBottom: 16,
          borderLeft: `5px solid ${isReject ? "#ef4444" : ACCENT}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
        styles={{ body: { padding: 18 } }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: "#617e9e",
              }}
            >
              Current Stage
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 700, color: ACCENT, marginTop: 2 }}>
              {statusLabel}
            </div>
            {statusText && (
              <div style={{ fontSize: "0.85rem", color: "#5b6f8c", marginTop: 6, maxWidth: 640 }}>
                {statusText}
              </div>
            )}
          </div>
          <Tag
            color={isReject ? "red" : "green"}
            style={{
              borderRadius: 40,
              fontSize: "0.75rem",
              fontWeight: 700,
              padding: "4px 14px",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {statusLabel}
          </Tag>
        </div>
      </Card>

      {/* Posting Filled — informs the applicant that the role is closed and
          blocks the interview-slot action (if any). Onboarding-stage
          messages still appear below. */}
      {isPostingFilled && !isReject && (
        <Card
          style={{
            borderRadius: 18,
            borderLeft: "5px solid #c26b2c",
            background: "#fff7ed",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            marginBottom: 16,
          }}
          styles={{ body: { padding: 18 } }}
        >
          <div style={{ fontWeight: 700, color: "#7a4a16", marginBottom: 6 }}>
            🔒 This role has been filled
          </div>
          <div style={{ fontSize: "0.9rem", color: "#7a4a16" }}>
            {"The hiring team has closed this posting. No further hiring actions are available here. If you've already been onboarded, those steps remain active."}
          </div>
        </Card>
      )}

      {/* The ONLY action: Interview slot picking during InterviewInProgress */}
      {showInterviewRounds && jobApplicationId && (
        <Card
          title={
            <span style={{ color: ACCENT, fontWeight: 700 }}>
              Interview Rounds
            </span>
          }
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <ApplicantInterviewRounds jobApplicationId={jobApplicationId} />
        </Card>
      )}

      {/* Friendly status messages for other stages */}
      {!showInterviewRounds && !isReject && !isPostingFilled && (
        <StageMessage status={status} />
      )}

      {isReject && (
        <Card
          style={{
            borderRadius: 18,
            borderLeft: "5px solid #ef4444",
            background: "#fef2f2",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
          styles={{ body: { padding: 18 } }}
        >
          <div style={{ fontWeight: 700, color: "#991b1b", marginBottom: 6 }}>
            Application Closed
          </div>
          <div style={{ fontSize: "0.9rem", color: "#7f1d1d" }}>
            {"Thank you for your interest. This application has been closed. Keep exploring — other opportunities are listed in the Job Openings tab."}
          </div>
        </Card>
      )}
    </>
  );
}

DetailContent.propTypes = {
  app: PropTypes.object.isRequired,
};

function StageMessage({ status }) {
  let icon = "📌";
  let bg = "#f0fdf4";
  let border = "#22c55e";
  let color = "#166534";
  let text =
    "Your application is being reviewed. We'll update this page as it progresses.";

  if (status === JobApplicationStatusEnum.InterviewCompleted) {
    icon = "✅";
    text =
      "All interview rounds are completed. The hiring team is finalizing feedback — we'll update you soon.";
  } else if (status === JobApplicationStatusEnum.FeedbackAdded) {
    icon = "📝";
    bg = "#eff6ff";
    border = "#3b82f6";
    color = "#1d4ed8";
    text =
      "Feedback has been recorded for your interviews. HR is preparing the next step.";
  } else if (status === JobApplicationStatusEnum.OnboardingDone) {
    icon = "🎉";
    text = "Onboarding is complete. Welcome to your new role!";
  } else if (status === JobApplicationStatusEnum.Hired) {
    icon = "🎯";
    text = "Congratulations — you've been hired into this role.";
  } else if (status === JobApplicationStatusEnum.ShortList) {
    icon = "⭐";
    text = "You've been shortlisted. HR will reach out with next steps soon.";
  }

  return (
    <Card
      style={{
        borderRadius: 18,
        borderLeft: `5px solid ${border}`,
        background: bg,
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
      styles={{ body: { padding: 18 } }}
    >
      <div style={{ fontSize: "0.92rem", color, fontWeight: 500 }}>
        <span style={{ marginRight: 8 }}>{icon}</span>
        {text}
      </div>
    </Card>
  );
}

StageMessage.propTypes = {
  status: PropTypes.number,
};

function HeroChip({ icon, text }) {
  return (
    <span
      style={{
        background: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.3)",
        color: "white",
        padding: "5px 12px",
        borderRadius: 40,
        fontSize: "0.78rem",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {icon} {text}
    </span>
  );
}

HeroChip.propTypes = {
  icon: PropTypes.node,
  text: PropTypes.string,
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
}

export default AppliedJobDetail;
